import { Injectable } from '@angular/core';
import { ApiService } from '@app/shared/services/api/api.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  DeviceValidationResponse,
  DeviceOnboardingRequest,
  DeviceReplacementValidationResponse,
  DeviceReplacementOnboardingRequest,
} from '../models/device-validation';
import { MeterViewResponse, MeterView } from '@app/shared/models/meter-data/meter-view';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable, throwError, of } from 'rxjs';
import { flatMap, isNil } from 'lodash';
import { MeterFilterService } from '@app/shared/services/meter-filter.service';
import * as moment from 'moment';
import { AuthService } from '@app/shared/services/auth.service';
import { SensorOnboardingResponse } from '../models/onboard-device';
import { StringUtility } from '@app/shared/string-utility';
import { SensorOffboardingResponse } from '../models/offboard-device';
import { SensorIconsResponse } from '../models/sensor-type.model';
import { IconDropdown } from '@app/shared/models/icon-dropdown';

@Injectable({
  providedIn: 'root',
})
export class SensorOnboardingService extends ApiService {
  private sensorTypeIcons$?: Observable<IconDropdown[]>;
  constructor(httpClient: HttpClient, private authService: AuthService, private meterFilterService: MeterFilterService) {
    super(httpClient);
  }

  private readonly basePath = `socrates/administration/api/onboarding`;
  private readonly meterApiBasePath = `socrates/meters/api/municipalities`;

  public validateDevice(deviceEui: string): Observable<DeviceValidationResponse> {
    const onboardingKey = JSON.stringify(deviceEui.trim().toLowerCase());
    return this.http.post<DeviceValidationResponse>(this.endpoint(this.basePath, `validate-device`), onboardingKey, {
      headers: new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      }),
    });
  }

  public validateReplacement(currentOnboardingKey: string, newOnboardingKey: string): Observable<DeviceReplacementValidationResponse> {
    const requestBody = {
      currentOnboardingKey: currentOnboardingKey.trim().toLowerCase(),
      newOnboardingKey: newOnboardingKey.trim().toLowerCase(),
    };
    return this.http.post<DeviceReplacementValidationResponse>(this.endpoint(this.basePath, `validate-replacement-device`), requestBody);
  }

  public validateMeter(meterNumber: string, municipalityKey: string): Observable<MeterView> {
    return this.getMeterByNumber(municipalityKey, meterNumber).pipe(
      filter((resp: MeterViewResponse) => !!resp && !!resp.data),
      map((resp: MeterViewResponse) => this.findMeter(resp.data, meterNumber)),
      switchMap((meter?: MeterView) => {
        if (!meter) {
          return throwError(() => new Error('Meter not found.'));
        }

        if (!isNil(meter.deviceId)) {
          return throwError(() => new Error('Meter already has an assigned device ID.'));
        }

        return of(meter);
      })
    );
  }

  public onboardDevice(body: DeviceOnboardingRequest): Observable<SensorOnboardingResponse> {
    return this.http.post<SensorOnboardingResponse>(this.endpoint(this.basePath, `onboard-device`), body);
  }

  public onboardReplacement(body: DeviceReplacementOnboardingRequest): Observable<string> {
    const httpOptions: Object = {
      responseType: 'text',
    };
    return this.http.post<string>(this.endpoint(this.basePath, `onboard-replacement-device`), body, httpOptions);
  }

  public associateSensorToAMeter(municipalityKey: string, meterId: string, sensorId: number, notes: string): Observable<Partial<MeterView>> {
    return this.http.patch<Partial<MeterView>>(this.endpoint(`${this.meterApiBasePath}/${municipalityKey}/meters/${meterId}`), {
      guid: meterId,
      deviceId: sensorId,
      notes: notes,
    });
  }

  public disAssociateSensorFromAMeter(municipalityKey: string, meterId: string, isIoTFeedOnly: boolean): Observable<Partial<MeterView>> {
    let body: Record<string, null | string | Date> = {
      guid: meterId,
      deviceId: null,
    };

    if (isIoTFeedOnly) {
      body = {
        ...body,
        usageEndDate: new Date(),
      };
    }

    return this.http.patch<Partial<MeterView>>(this.endpoint(`${this.meterApiBasePath}/${municipalityKey}/meters/${meterId}`), body);
  }

  public getMeterBySensorId(municipalityKey: string, sensorID: string): Observable<MeterView[]> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);
    const q = `?$filter=municipalityKey eq '${municipalityKey}' and deviceId eq ${sensorID}`;

    return this.http.get<MeterView[]>(url + q);
  }

  private getMeterByNumber(municipalityKey: string, meterNumber: string): Observable<MeterViewResponse> {
    let q = '';
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);
    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const meterNumberFilter = this.meterFilterService.setMeterNumberFilter(meterNumber);
    if (meterNumberFilter.length > 0) {
      q += ` and ${meterNumberFilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    q += `&$count=true`;

    return this.http.get<MeterViewResponse>(url + `?${q}`);
  }

  /*
    /datapool is deprecated. 
  */
  /*
  createMeter(meter: MeterCreateRequest): Observable<any> {
    const url = this.endpoint(`datapool/meters`);
    const body = assign(
      {
        isImsysInstalled: false,
        isEnergyGuardLicenseAvailable: false,
        converterFactor: 1,
        plantNumber: generateGUID().replace(/-/g, ''),
        measurementLocationKey: generateGUID(),
        isIOTFeedOnly: true,
        komKey: 'KO2',
      },
      meter.location,
      meter
    );
    delete body.location;
    return this.http.post<any>(encodeURI(url), body);
  }
  */

  private findMeter(data: MeterView[], meterNumber: string): MeterView | undefined {
    return data.find((meter: MeterView) =>
      StringUtility.areStringsIdentical(StringUtility.removeLeadingZeroes(meter.meterNumber), StringUtility.removeLeadingZeroes(meterNumber))
    );
  }

  public offboardDevice(sensorId: any, municipalityKey: string): Observable<SensorOffboardingResponse> {
    const offboardDTO = {
      deviceId: sensorId,
      isActive: false,
      modifyDate: moment.utc().toDate(),
      modifyUser: this.authService.currentUser.guid,
    };
    const patchOnboardingUrl = `socrates/administration/api/municipalities/${municipalityKey}/devices/${sensorId}`;

    return this.http.patch<SensorOffboardingResponse>(this.endpoint(patchOnboardingUrl), offboardDTO);
  }

  public getSensorTypeIcons(): Observable<IconDropdown[]> {
    if (!this.sensorTypeIcons$) {
      this.sensorTypeIcons$ = this.http.get<SensorIconsResponse[]>(this.endpoint(`socrates/administration/api/references/icons`)).pipe(
        map((response: SensorIconsResponse[]): IconDropdown[] =>
          flatMap(
            response.map((icons: SensorIconsResponse) => ({
              name: icons.name,
              displayName: icons.description,
              iconPath: `./assets/icons/sensorTypes/${icons.fileName}`,
            }))
          )
        ),
        map((icons: IconDropdown[]): IconDropdown[] => {
          icons.sort((a, b) => {
            if (a.name === 'defaultsensor') return -1;
            if (b.name === 'defaultsensor') return 1;
            return 0;
          });

          return icons;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.sensorTypeIcons$;
  }
}
